import { forwardRef } from "react";

import { useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { twMerge } from "@/utils/twMerge";

import type { View } from "@/components/expression/utils/NodeStyleProvider";
import type { NodeSize } from "@corca/data";
import type { ComponentProps, ForwardedRef, PropsWithChildren } from "react";

export type Dimension = "square" | "narrow" | "tight";

type ClassNames = Record<NodeSize, Record<Dimension, string>>;

const BASE_DIMENSIONS: ClassNames = {
  sm: {
    square: "min-h-[21px] min-w-[21px] px-[5px]",
    narrow: "min-h-[21px] min-w-[17px] px-[4px]",
    tight: "min-h-[21px] min-w-[12px] px-[2px]",
  },
  xs: {
    square: "min-h-[16px] min-w-[16px] px-[2px]",
    narrow: "min-h-[16px] min-w-[14px] px-[2px]",
    tight: "min-h-[16px] min-w-[10px] px-[1px]",
  },
  xxs: {
    square: "min-h-[14px] min-w-[14px] px-[2px]",
    narrow: "min-h-[14px] min-w-[14px] px-[2px]",
    tight: "min-h-[14px] min-w-[10px] px-0",
  },
  xxxs: {
    square: "min-h-[14px] min-w-[14px] px-[2px]",
    narrow: "min-h-[14px] min-w-[14px] px-[2px]",
    tight: "min-h-[14px] min-w-[10px] px-0",
  },
};

const TEXT_DIMENSIONS: ClassNames = {
  sm: {
    square: "min-h-[21px] min-w-auto px-[2px]",
    narrow: "min-h-[21px] min-w-auto px-[2px]",
    tight: "min-h-[21px] min-w-auto px-0",
  },
  xs: {
    square: "min-h-[16px] min-w-auto px-[1px]",
    narrow: "min-h-[16px] min-w-auto px-[1px]",
    tight: "min-h-[16px] min-w-auto px-0",
  },
  xxs: {
    square: "min-h-[10px] min-w-[6px]",
    narrow: "min-h-[10px] min-w-[6px]",
    tight: "min-h-[10px] min-w-[6px]",
  },
  xxxs: {
    square: "min-h-auto min-w-auto",
    narrow: "min-h-auto min-w-auto",
    tight: "min-h-auto min-w-auto",
  },
};

export const DIMENSIONS_CLASSES: Record<View, ClassNames> = {
  expression: Object.fromEntries(
    Object.entries(BASE_DIMENSIONS).map(([size, dimensions]) => [size, dimensions]),
  ) as ClassNames,
  "compact-expression": Object.fromEntries(
    Object.entries(BASE_DIMENSIONS).map(([size, dimensions]) => [size, dimensions]),
  ) as ClassNames,
  text: Object.fromEntries(
    Object.entries(TEXT_DIMENSIONS).map(([size, dimensions]) => [size, dimensions]),
  ) as ClassNames,
};

export function useDimensionsClassNames(dimension: Dimension = "square"): string {
  const { size, view } = useNodeStyle();
  return DIMENSIONS_CLASSES[view][size][dimension];
}

type Props = {
  dimension?: Dimension;
} & PropsWithChildren<ComponentProps<"div">>;

export const NodeDimensions = forwardRef(function NodeDimensions(
  { children, className, dimension = "square", ...props }: Props,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const dimensionsClassNames = useDimensionsClassNames(dimension);

  return (
    <div ref={ref} className={twMerge(dimensionsClassNames, className)} {...props}>
      {children}
    </div>
  );
});
